import axios, { AxiosError, AxiosResponse } from "axios";
import { apiBaseUrl } from "..";
import {
  AppAboutItemsResponse,
  AppOpensResponse,
  AttractorContentClassificationRequest,
  AttractorContentType,
  CsvMessageRow,
  DailyActiveUsersResponse,
  DailyGeoActiveUsersResponse,
  DailyVsGeoUsersResponse,
  DauSessionsResponse,
  DeviceBreakdownResponse,
  FlashHistoryResponse,
  FlashMessageBody,
  GeoAnalyticsResponse,
  GeofenceResponse,
  GeoFencesResponse,
  KioskAttractorsResponse,
  KioskHealthAllResponse,
  MobileAttractorsResponse,
  MobileBannersResponse,
  NotifScheduleAllResponse,
  PromotionSearchResponse,
  StickinessResponse,
  TierOptions,
  TotalStatsResponse,
  UpdateMobileBannerRequest,
  UserRetentionResponse,
  VenueListResponse,
  VenueSetupStatusResponse,
} from "../types";
import { userState } from "../state/UserState";
import UserStore from "../store/UserStore";

export const sendVenueFlashMessage = async (
  body: FlashMessageBody
): Promise<void> => {
  const url = `${apiBaseUrl}/v1/venue-admin/${userState.getVenueId}/create-flash-message/venue`;

  try {
    return await axios.post(url, body);
  } catch (error) {
    if (error instanceof AxiosError) {
      console.error("Error sending venue flash message", error.message);
      console.error("Response data:", error.response?.data);
    } else {
      console.error("Unexpected error:", error);
    }
  }
};

export const getTierOptions = async (): Promise<TierOptions> => {
  const url = `${apiBaseUrl}/v1/venue-admin/${userState.getVenueId}/get-filter-options`;

  try {
    const res = await axios.get(url);
    return res?.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      console.error("Error getting tier options", error.message);
      console.error("Response data:", error.response?.data);
    } else {
      console.error("Unexpected error:", error);
    }
    throw error;
  }
};

export const sendCustomFlashMessage = async ({
  title,
  message,
  tierOption,
}: {
  title: string;
  message: string;
  tierOption: string;
}): Promise<AxiosResponse<any>> => {
  const url = `${apiBaseUrl}/v1/venue-admin/${userState.getVenueId}/create-flash-message/filtered`;

  try {
    return await axios.post(url, {
      tierOption,
      title,
      message,
    });
  } catch (error) {
    if (error instanceof AxiosError) {
      console.error("Error sending custom flash message", error.message);
      console.error("Response data:", error.response?.data);
    } else {
      console.error("Unexpected error:", error);
    }
    throw error;
  }
};

export const scheduleNotification = async ({
  scheduleType,
  notificationType,
  notificationTitle,
  notificationMessage,
  notificationScheduleTime,
  tierOption,
}: {
  scheduleType: string;
  notificationType?: string;
  notificationTitle: string;
  notificationMessage: string;
  notificationScheduleTime: number;
  tierOption?: string;
}): Promise<AxiosResponse<any>> => {
  const url = `${apiBaseUrl}/v1/venue-admin/${
    userState.getVenueId
  }/u/${UserStore.getUserId()}/notif-schedule/create`;

  let body: any = {
    scheduleType,
    notificationType,
    notificationTitle,
    notificationMessage,
    notificationScheduleTime,
  };

  if (tierOption) {
    body.tierOption = tierOption;
  }

  try {
    return await axios.post(url, body);
  } catch (error) {
    if (error instanceof AxiosError) {
      console.error("Error scheduling notification", error.message);
      console.error("Response data:", error.response?.data);
    } else {
      console.error("Unexpected error:", error);
    }
    throw error;
  }
};

export const cancelScheduledNotification = async ({
  scheduledNotificationId,
}: {
  scheduledNotificationId: number;
}): Promise<AxiosResponse<any>> => {
  const url = `${apiBaseUrl}/v1/venue-admin/${
    userState.getVenueId
  }/u/${UserStore.getUserId()}/notif-schedule/${scheduledNotificationId}/cancel`;

  try {
    return await axios.post(url);
  } catch (error) {
    if (error instanceof AxiosError) {
      console.error("Error canceling scheduled notification", error.message);
      console.error("Response data:", error.response?.data);
    } else {
      console.error("Unexpected error:", error);
    }
    throw error;
  }
};

export const getKioskAttractors = async (): Promise<
  AxiosResponse<KioskAttractorsResponse>
> => {
  const url = `${apiBaseUrl}/v1/venue-admin/${userState.getVenueId}/kiosk/attractors`;
  try {
    const response = await axios.get(url);
    return response;
  } catch (error) {
    if (error instanceof AxiosError) {
      console.error("Error getting kiosk attractors", error.message);
      console.error("Response data:", error.response?.data);
    } else {
      console.error("Unexpected error:", error);
    }
    throw error;
  }
};
export const getSingleUploadPresignedS3Url = async ({
  imageFileName,
}: {
  imageFileName: string;
}) => {
  try {
    const response = await axios.post(
      `${apiBaseUrl}/v1/user/${UserStore.getUserId()}/venue-admin/${
        userState.getVenueId
      }/upload-url-single`,
      { imageFileName }
      // { cancelToken: cancelToken.token }
    );
    return response;
  } catch (error: any) {
    if (error instanceof AxiosError) {
      console.error("Error uploading image", error.message);
      console.error("Response data:", error.response?.data);
    } else {
      console.error("Unexpected error:", error);
    }
    throw error;
  }
};

export const uploadToS3PresignedUrl = async ({
  uploadUrl,
  data,
  progressCallback,
}: {
  uploadUrl: string;
  data: any;
  progressCallback: (percentCompleted: number) => void;
}) => {
  const config = {
    onUploadProgress: (progressEvent: any) => {
      const percentCompleted = Math.round(
        (progressEvent.loaded * 100) / progressEvent.total
      );
      progressCallback(percentCompleted);
    },
    withCredentials: false,
  };

  return axios.put(uploadUrl, data, config);
};

export const createKioskAttractor = ({
  mediaS3Key,
  attractor,
}: {
  mediaS3Key: string;
  attractor: {
    startDateEpoch: number;
    endDateEpoch: number;
    dayVisibility: string;
  } & Partial<AttractorContentClassificationRequest>;
}) => {
  return axios
    .post(
      `${apiBaseUrl}/v1/venue-admin/${userState.getVenueId}/kiosk/create-attractor`,
      {
        mediaS3Key,
        ...attractor,
      }
    )
    .catch((error: any) => {});
};

export const getKioskAllHealth = (): Promise<
  AxiosResponse<KioskHealthAllResponse>
> => {
  return axios.get(
    `${apiBaseUrl}/v1/venue-admin/${userState.getVenueId}/kiosk/health-all`
  );
};

export const deleteKioskAttractor = ({
  attractorId,
}: {
  attractorId: number | string;
}) => {
  return axios.post(
    `${apiBaseUrl}/v1/venue-admin/${userState.getVenueId}/kiosk/delete-attractor/${attractorId}`
  );
};

export const updateKioskAttractor = ({
  attractorId,
  startDateEpoch,
  endDateEpoch,
  dayVisibility,
  ...classification
}: {
  attractorId: number | string;
  startDateEpoch: number;
  endDateEpoch: number;
  dayVisibility: string;
} & Partial<AttractorContentClassificationRequest>) => {
  return axios.post(
    `${apiBaseUrl}/v1/venue-admin/${userState.getVenueId}/kiosk/update-attractor/${attractorId}`,
    {
      startDateEpoch: startDateEpoch,
      endDateEpoch: endDateEpoch,
      dayVisibility: dayVisibility,
      ...classification,
    }
  );
};

/**
 * Games or promotions at a venue that an attractor can link to. Returns the full list for the
 * venue; the caller filters it by name.
 */
export const searchVenuePromotions = (
  venueId: number,
  contentType: Exclude<AttractorContentType, "General">
): Promise<AxiosResponse<PromotionSearchResponse>> => {
  return axios.get(
    `${apiBaseUrl}/v1/venue-admin/${venueId}/promotions/search`,
    { params: { contentType } }
  );
};

export const getMobileAttractors = (): Promise<
  AxiosResponse<MobileAttractorsResponse>
> => {
  return axios.get(
    `${apiBaseUrl}/v1/venue-admin/${userState.getVenueId}/mobile/attractors`
  );
};
export const getAppBanners = (): Promise<
  AxiosResponse<MobileBannersResponse>
> => {
  return axios.get(
    `${apiBaseUrl}/v1/venue-admin/${userState.getVenueId}/mobile/banners`
  );
};
export const getAppAboutUsItems = (): Promise<
  AxiosResponse<AppAboutItemsResponse>
> => {
  return axios.get(
    `${apiBaseUrl}/v1/venue-admin/${userState.getVenueId}/app-about-items`
  );
};

export const deleteAppAboutUsItem = ({
  aboutUsItemId,
}: {
  aboutUsItemId: string;
}) => {
  return axios.post(
    `${apiBaseUrl}/v1/venue-admin/${userState.getVenueId}/delete-app-about-item/${aboutUsItemId}`
  );
};

export const updateAppAboutUsItem = ({
  aboutUsItemId,
  newItemDisplayText,
  newItemUrl,
}: {
  aboutUsItemId: string;
  newItemDisplayText: string;
  newItemUrl: string;
}) => {
  return axios.post(
    `${apiBaseUrl}/v1/venue-admin/${userState.getVenueId}/update-app-about-item/${aboutUsItemId}`,
    {
      displayText: newItemDisplayText,
      displayLinkUrl: newItemUrl,
    }
  );
};

export const createAppAboutUsItem = ({
  displayText,
  displayLinkUrl,
  attachmentType,
  imageKey,
}: {
  displayText: string;
  displayLinkUrl: string;
  attachmentType: string;
  imageKey?: string;
}) => {
  return axios
    .post(
      `${apiBaseUrl}/v1/venue-admin/${userState.getVenueId}/create-app-about-item`,
      {
        displayText,
        displayLinkUrl,
        attachmentType,
        imageKey,
      }
    )
    .catch((error: any) => {});
};

export const createAppBanner = ({
  mediaThumbS3Key,
  mediaFullS3Key,
  displayLinkUrl,
  startDateEpoch,
  endDateEpoch,
  dayVisibility,
  bannerType,
}: {
  mediaThumbS3Key: string;
  mediaFullS3Key: string;
  displayLinkUrl: string;
  startDateEpoch: number;
  endDateEpoch: number;
  dayVisibility: string;
  bannerType: string;
}) => {
  return axios
    .post(
      `${apiBaseUrl}/v1/venue-admin/${userState.getVenueId}/mobile/banners/create`,
      {
        mediaThumbS3Key,
        mediaFullS3Key,
        displayLinkUrl,
        startDateEpoch,
        endDateEpoch,
        dayVisibility,
        bannerType,
      }
    )
    .catch((error: any) => {});
};

export const createMobileAttractor = (requestBody: {
  mediaThumbS3Key: string;
  mediaFullS3Key: string;
  displayLinkUrl: string;
  startDateEpoch: number;
  endDateEpoch: number;
  dayVisibility: string;
  thumbnailWidth: number;
  thumbnailHeight: number;
  fullscreenWidth: number;
  fullscreenHeight: number;
  displayText: string;
  isIntegrationUrl: boolean;
  specificUserTier?: string;
  attractorType: string;
}) => {
  return axios
    .post(
      `${apiBaseUrl}/v1/venue-admin/${userState.getVenueId}/mobile/attractors/create`,
      requestBody
    )
    .catch((error: any) => {});
};

export const deleteAppBanner = ({
  appBannerId,
}: {
  appBannerId: number | string;
}) => {
  return axios.post(
    `${apiBaseUrl}/v1/venue-admin/${userState.getVenueId}/delete-app-banner/${appBannerId}`
  );
};

export const deleteMobileAttractor = ({
  attractorId,
}: {
  attractorId: number | string;
}) => {
  return axios.post(
    `${apiBaseUrl}/v1/venue-admin/${userState.getVenueId}/mobile-attractors/delete/${attractorId}`
  );
};

export const updateAppBanner = ({
  appBannerId,
  displayLinkUrl,
  startDateEpoch,
  endDateEpoch,
  dayVisibility,
}: UpdateMobileBannerRequest) => {
  return axios.post(
    `${apiBaseUrl}/v1/venue-admin/${userState.getVenueId}/update-app-banner/${appBannerId}`,
    {
      displayLinkUrl,
      startDateEpoch,
      endDateEpoch,
      dayVisibility,
    }
  );
};

export const updateMobileAttractor = ({
  mobileAttractorId,
  displayText,
  displayLinkUrl,
  startDateEpoch,
  endDateEpoch,
  dayVisibility,
  specificUserTier,
}: {
  mobileAttractorId: number;
  displayText: string;
  displayLinkUrl: string;
  startDateEpoch: number;
  endDateEpoch: number;
  dayVisibility: string;
  specificUserTier?: string;
}) => {
  return axios.post(
    `${apiBaseUrl}/v1/venue-admin/${userState.getVenueId}/mobile-attractors/update/${mobileAttractorId}`,
    {
      displayText,
      displayLinkUrl,
      startDateEpoch,
      endDateEpoch,
      dayVisibility,
      specificUserTier,
    }
  );
};

export const swapMobileAttractorSortKeys = (
  firstAttractorSortKey: number,
  secondAttractorSortKey: number
) => {
  return axios.post(
    apiBaseUrl +
      "/v1/venue-admin/" +
      userState.getVenueId +
      "/mobile-attractors/swap-order",
    {
      firstAttractorSortKey,
      secondAttractorSortKey,
    }
  );
};

export const getValidVenues = (): Promise<AxiosResponse<VenueListResponse>> => {
  return axios.get(`${apiBaseUrl}/v1/pe-admin/venue-list`);
};

export const getVenueSetupStatusData = (
  venueId: string
): Promise<AxiosResponse<VenueSetupStatusResponse>> => {
  return axios.get(
    `${apiBaseUrl}/v1/pe-admin/user/${userState.user?.userId}/venue/${venueId}/setup-status`
  );
};

export const getGeofencesByVenueId = ({
  venueId,
}: {
  venueId: string | number;
}) => {
  return axios.get(apiBaseUrl + "/v1/pe-admin/geofences/" + venueId);
};

export const getAllGeofences = (): Promise<AxiosResponse<GeofenceResponse>> => {
  return axios.get(apiBaseUrl + "/v1/pe-admin/geofences");
};

export const deleteVenueGeofence = ({
  venueId,
  locationId,
}: {
  venueId: string;
  locationId: string;
}) => {
  return axios.post(
    `${apiBaseUrl}/v1/pe-admin/${venueId}/geofences/${locationId}/del`
  );
};
export const addVenueGeofence = ({
  venueId,
  locationId,
}: {
  venueId: string;
  locationId: string;
}) => {
  return axios.post(
    `${apiBaseUrl}/v1/pe-admin/${venueId}/geofences/${locationId}/add`
  );
};

export const getGeoAnalyticsData = ({
  daysAgo,
  hoursAgo,
}: {
  daysAgo: number;
  hoursAgo: number;
}): Promise<AxiosResponse<GeoAnalyticsResponse>> => {
  return axios.post(
    `${apiBaseUrl}/v1/venue-admin/${userState.getVenueId}/geo/analytics`,
    {
      daysAgo: daysAgo,
      hoursAgo: hoursAgo,
    }
  );
};

export const getVenueGeofences = (): Promise<
  AxiosResponse<GeoFencesResponse>
> =>
  axios.get(`${apiBaseUrl}/v1/venue-admin/${userState.getVenueId}/geo/fences`);

export const getSignUpDayData = (
  { dayCount }: { dayCount: number },
  signal?: AbortSignal
) => {
  return axios.get(
    apiBaseUrl +
      "/v1/venue-admin/" +
      userState.getVenueId +
      "/signups?daysAgo=" +
      dayCount,
    { signal }
  );
};

export const getAppOpenDayData = (
  {
    dayCount,
  }: {
    dayCount: number;
  },
  signal?: AbortSignal
): Promise<AxiosResponse<AppOpensResponse>> => {
  return axios.get(
    apiBaseUrl +
      "/v1/venue-admin/" +
      userState.getVenueId +
      "/appopens?daysAgo=" +
      dayCount,
    { signal }
  );
};

export const getActiveUsersData = (
  {
    dayCount,
  }: {
    dayCount: number;
  },
  signal?: AbortSignal
): Promise<AxiosResponse<DailyActiveUsersResponse>> => {
  return axios.get(
    apiBaseUrl +
      "/v1/venue-admin/" +
      userState.getVenueId +
      "/daily-active-users?daysAgo=" +
      dayCount,
    { signal }
  );
};

export const getGeoActiveUsersData = (
  {
    dayCount,
  }: {
    dayCount: number;
  },
  signal?: AbortSignal
): Promise<AxiosResponse<DailyGeoActiveUsersResponse>> => {
  return axios.get(
    apiBaseUrl +
      "/v1/venue-admin/" +
      userState.getVenueId +
      "/daily-geo-active-users?daysAgo=" +
      dayCount,
    { signal }
  );
};
export const getDailySessionsPerDAU = (
  {
    dayCount,
  }: {
    dayCount: number;
  },
  signal?: AbortSignal
): Promise<AxiosResponse<DauSessionsResponse>> => {
  return axios.get(
    apiBaseUrl +
      "/v1/venue-admin/" +
      userState.getVenueId +
      "/dau-sessions?daysAgo=" +
      dayCount,
    { signal }
  );
};
export const getActiveUsersVsGeoActiveUsersData = (
  {
    dayCount,
  }: {
    dayCount: number;
  },
  signal?: AbortSignal
): Promise<AxiosResponse<DailyVsGeoUsersResponse>> => {
  return axios.get(
    apiBaseUrl +
      "/v1/venue-admin/" +
      userState.getVenueId +
      "/daily-vs-geo-users?daysAgo=" +
      dayCount,
    { signal }
  );
};
export const getStickinessData = (
  {
    dayCount,
  }: {
    dayCount: number;
  },
  signal?: AbortSignal
): Promise<AxiosResponse<StickinessResponse>> => {
  return axios.get(
    apiBaseUrl +
      "/v1/venue-admin/" +
      userState.getVenueId +
      "/stickiness?daysAgo=" +
      dayCount,
    { signal }
  );
};

export const getUserRetention = (): Promise<
  AxiosResponse<UserRetentionResponse>
> => {
  return axios.get(
    apiBaseUrl + "/v1/venue-admin/" + userState.getVenueId + "/user-retention"
  );
};

export const getTotalStats = (
  signal?: AbortSignal
): Promise<AxiosResponse<TotalStatsResponse>> => {
  return axios.get(
    apiBaseUrl + "/v1/venue-admin/" + userState.getVenueId + "/total-stats",
    { signal }
  );
};
export const getDeviceBreakdown = (
  signal?: AbortSignal
): Promise<AxiosResponse<DeviceBreakdownResponse>> => {
  return axios.get(
    apiBaseUrl +
      "/v1/venue-admin/" +
      userState.getVenueId +
      "/device-breakdown",
    { signal }
  );
};

export const addNewGeofence = ({
  name,
  coordinates,
}: {
  name: string;
  coordinates: any;
}) => {
  return axios.post(apiBaseUrl + "/v1/pe-admin/add-geofence", {
    name,
    coordinates,
  });
};

export const deleteGeofence = (locationId: string) => {
  return axios.post(apiBaseUrl + "/v1/pe-admin/delete-geofence/" + locationId);
};

export function updateGeofence(
  locationId: string,
  name: string,
  updatedCoordinates: any[]
) {
  return axios.post(
    apiBaseUrl + "/v1/pe-admin/update-geofence/" + locationId,
    {
      name: name,
      coordinates: updatedCoordinates,
    },
    {
      headers: {
        "Content-Type": "application/json;charset=UTF-8",
      },
    }
  );
}

export const verifySingleRecipient = (badgeNumber: string) => {
  return axios.post(
    apiBaseUrl +
      "/v1/venue-admin/" +
      userState.getVenueId +
      "/verify-badge-number/" +
      badgeNumber
  );
};

export const sendSingleUserFlashMessage = ({
  userId,
  title,
  message,
}: {
  title: string;
  message: string;
  userId: string;
}) => {
  return axios.post(
    apiBaseUrl +
      "/v1/venue-admin/" +
      userState.getVenueId +
      "/create-flash-message/single-user",
    {
      userId,
      title,
      message,
    }
  );
};

export const getScheduledNotifications = (): Promise<
  AxiosResponse<NotifScheduleAllResponse>
> => {
  return axios.get(
    apiBaseUrl +
      "/v1/venue-admin/" +
      userState.getVenueId +
      "/u/" +
      UserStore.getUserId() +
      "/notif-schedule/all"
  );
};

export const getFlashHistory = (): Promise<
  AxiosResponse<FlashHistoryResponse>
> => {
  return axios.get(
    apiBaseUrl + "/v1/venue-admin/" + userState.getVenueId + "/flash-history"
  );
};

export const updateMobileAppBannerTimer = (intervalSecs: number) => {
  return axios.post(
    apiBaseUrl +
      "/v1/venue-admin/" +
      userState.getVenueId +
      "/app-banners/update-interval",
    {
      intervalSecs,
    }
  );
};

export const updateKioskAttractorTimer = ({
  KioskTimeUntilBackgroundModeSecs,
  KioskTimeToRefreshBackendDataSecs,
  KioskTimeToChangeAttractorSecs,
}: {
  KioskTimeUntilBackgroundModeSecs: number;
  KioskTimeToRefreshBackendDataSecs: number;
  KioskTimeToChangeAttractorSecs: number;
}) => {
  return axios.post(
    apiBaseUrl +
      "/v1/venue-admin/" +
      userState.getVenueId +
      "/kiosk/kiosk-config/update",
    {
      KioskTimeUntilBackgroundModeSecs,
      KioskTimeToRefreshBackendDataSecs,
      KioskTimeToChangeAttractorSecs,
    }
  );
};

export const saveVenueIcon = (iconKey: string, venueId: string) => {
  return axios.post(
    `${apiBaseUrl}/v1/pe-admin/user/${UserStore.getUserId()}/venue/${venueId}/set-icon`,
    {
      S3Key: iconKey,
    }
  );
};

export const saveVenueAppBackground = (backgroundKey: string) => {
  return axios.post(
    `${apiBaseUrl}/v1/pe-admin/user/${UserStore.getUserId()}/venue/${
      userState.getVenueId
    }/set-app-background`,
    {
      S3Key: backgroundKey,
    }
  );
};

export const saveEnableVideoAttractor = ({
  videoAttractorsEnabled,
  venueId,
}: {
  videoAttractorsEnabled: boolean;
  venueId: string;
}) => {
  return axios.post(
    `${apiBaseUrl}/v1/pe-admin/user/${UserStore.getUserId()}/venue/${venueId}/toggle-video-attractors`,
    {
      VideoAttractorsEnabled: videoAttractorsEnabled,
    }
  );
};

export const updateVenueSetupStatusData = (
  venueId: string,
  requestBody: {
    [key: string]: any;
  }
) => {
  return axios.post(
    `${apiBaseUrl}/v1/pe-admin/user/${UserStore.getUserId()}/venue/${venueId}/update-venue`,
    requestBody
  );
};

export const sendCsvFlashMessage = (recipients: CsvMessageRow[]) => {
  return axios.post(
    apiBaseUrl +
      "/v1/venue-admin/" +
      userState.getVenueId +
      "/create-flash-message/csv",
    {
      recipients: recipients,
    }
  );
};
